import React, { useContext } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Hero from "../components/Hero";
import Meta from "../Meta";
import Loader from "../loader";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { TravelThemeContext } from "../index";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import Link from "next/link";
import { getBethHabadLabel } from "../utils";

const bethHabadStyles = `
/* ── Layout ── */
.travel-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Section commune ── */
.tr-section {
  margin-top: 48px;
}
.tr-section__head {
  background: #f3f4f6;
  border-radius: 10px;
  padding: 14px 20px;
  font-weight: 700;
  font-size: 15px;
  color: #1a1a2e;
  margin-bottom: 24px;
  text-align: center;
}

/* ── Shluhim ── */
.tr-shluhim {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
}
.tr-shluhim__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 180px;
}
.tr-shluhim__avatar {
  width: 72px; height: 72px;
  border-radius: 50%;
  object-fit: cover;
  background: #e5e7eb;
}
.tr-shluhim__avatar--placeholder {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex; align-items: center; justify-content: center;
  color: #9ca3af;
  font-size: 28px;
  font-weight: 600;
}
.tr-shluhim__name {
  font-weight: 700;
  font-size: 15px;
  color: #1a1a2e;
  text-align: center;
}
.tr-shluhim__role {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
}
.tr-shluhim__actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}
.tr-shluhim__action {
  width: 30px; height: 30px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  text-decoration: none;
  transition: opacity .15s;
}
.tr-shluhim__action:hover { opacity: .75; }
.tr-shluhim__action--phone   { background: #ede9fe; color: #7c3aed !important; }
.tr-shluhim__action--email   { background: #fce7f3; color: #db2777 !important; }
.tr-shluhim__action--whatsapp{ background: #dcfce7; color: #16a34a !important; }
.tr-shluhim__action svg { color: inherit; stroke: currentColor; }

/* ── Adresse / Map ── */
.tr-address {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.tr-address__label {
  font-size: 14px;
  color: #6b7280;
}
.tr-address__line {
  font-size: clamp(17px, 2.2vw, 20px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 1px;
}
.tr-address__cta {
  margin-top: 12px;
}
.tr-address__map {
  margin-top: 24px;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  background: #f3f4f6;
}
@media (max-width: 640px) {
  .tr-address__map { aspect-ratio: 4 / 3; }
}

/* ── Contact split layout ── */
.tr-contact-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}
@media (max-width: 700px) {
  .tr-contact-wrap {
    grid-template-columns: 1fr;
  }
}

/* ── Org title ── */
.tr-bh__org-title {
  font-size: clamp(22px, 4vw, 32px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 32px 0 0;
}

/* ── About ── */
.tr-bh__about {
  max-width: 760px;
  margin: 0 auto;
  font-size: 15px;
  line-height: 1.8;
  color: #1f2937;
}
.tr-bh__about h1,
.tr-bh__about h2,
.tr-bh__about h3 {
  color: #1a1a2e;
  margin-top: 24px;
  margin-bottom: 10px;
}
.tr-bh__about p { margin: 0 0 14px; }
.tr-bh__about a { color: var(--primary, #1b579e); text-decoration: underline; }
.tr-bh__about ul,
.tr-bh__about ol { margin: 0 0 14px; padding-inline-start: 22px; }
.tr-bh__about img { max-width: 100%; height: auto; border-radius: 12px; }

/* ── Contact list (left) ── */
.tr-contact-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.tr-contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #374151;
  text-decoration: none;
}
.tr-contact-item:hover {
  color: var(--primary, #1b579e);
}
.tr-contact-item__icon {
  width: 34px; height: 34px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
}
.tr-contact-item__icon--phone {
  background: #ede9fe;
  color: #7c3aed;
}
.tr-contact-item__icon--whatsapp {
  background: #dcfce7;
  color: #16a34a;
}
.tr-contact-item__icon--email {
  background: #fce7f3;
  color: #db2777;
}
.tr-contact-item__label {
  line-height: 1.3;
}
.tr-contact-item__sublabel {
  display: block;
  font-size: 12px;
  color: #9ca3af;
}

/* ── Contact form (right) ── */
.tr-contact-form__faq-hint {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 16px;
}
.tr-contact-form__faq-hint a {
  color: var(--primary, #1b579e);
  text-decoration: none;
}
.tr-contact-form__faq-hint a:hover { text-decoration: underline; }
.tr-contact-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.tr-contact-form__field {
  margin-bottom: 10px;
}
.tr-contact-form__label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}
.tr-contact-form__label span {
  color: #ef4444;
  font-size: 11px;
  margin-inline-start: 3px;
}
.tr-contact-form__input,
.tr-contact-form__textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color .15s;
  box-sizing: border-box;
  background: white;
}
.tr-contact-form__input:focus,
.tr-contact-form__textarea:focus {
  border-color: var(--primary, #1b579e);
}
.tr-contact-form__textarea {
  min-height: 96px;
  resize: vertical;
}
.tr-contact-form__submit {
  margin-top: 6px;
}
`;

function ContactIcon({ type }) {
    const iconMap = {
        phone: { icon: { name: "IconPhone", source: "tabler" }, cls: "tr-contact-item__icon--phone" },
        whatsapp: { icon: { name: "IconBrandWhatsapp", source: "tabler" }, cls: "tr-contact-item__icon--whatsapp" },
        email: { icon: { name: "IconMail", source: "tabler" }, cls: "tr-contact-item__icon--email" },
    };
    const entry = iconMap[type] || { icon: { name: "IconLink", source: "tabler" }, cls: "" };
    return (
        <span className={`tr-contact-item__icon ${entry.cls}`}>
            {renderIcon(entry.icon, 18)}
        </span>
    );
}

function ContactForm({ context }) {
    const [form, setForm] = React.useState({ firstName: "", lastName: "", email: "", phone: "", subject: "", message: "" });
    const [sent, setSent] = React.useState(false);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleSubmit = (e) => {
        e.preventDefault();
        setSent(true);
    };

    if (sent) {
        return (
            <div style={{ padding: "32px 0", textAlign: "center", color: "#16a34a", fontWeight: 600 }}>
                <Trans>Votre message a été envoyé !</Trans>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="tr-contact-form__faq-hint">
                <Trans>Avez-vous essayé notre zone FAQ (questions fréquemment posées) ?</Trans>
                {" "}
                <Link href={context.org.BASE_WEB_URL+"/travel/faq"} legacyBehavior><a><Trans>Vous pouvez trouver la réponse ici</Trans></a></Link>
            </div>

            <div className="tr-contact-form__row">
                <div>
                    <label className="tr-contact-form__label">
                        <Trans>Prénom</Trans> <span><Trans>(Nécessaire)</Trans></span>
                    </label>
                    <input className="tr-contact-form__input" value={form.firstName} onChange={e => set("firstName", e.target.value)} required />
                </div>
                <div>
                    <label className="tr-contact-form__label"><Trans>Nom de famille</Trans></label>
                    <input className="tr-contact-form__input" value={form.lastName} onChange={e => set("lastName", e.target.value)} />
                </div>
            </div>

            <div className="tr-contact-form__row">
                <div>
                    <label className="tr-contact-form__label">
                        <Trans>E-mail</Trans> <span><Trans>(Nécessaire)</Trans></span>
                    </label>
                    <input className="tr-contact-form__input" type="email" value={form.email} onChange={e => set("email", e.target.value)} required />
                </div>
                <div>
                    <label className="tr-contact-form__label">
                        <Trans>Numéro de téléphone</Trans> <span><Trans>(Nécessaire)</Trans></span>
                    </label>
                    <input className="tr-contact-form__input" type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} />
                </div>
            </div>

            <div className="tr-contact-form__field">
                <label className="tr-contact-form__label"><Trans>Objet de l'e-mail</Trans></label>
                <input className="tr-contact-form__input" value={form.subject} onChange={e => set("subject", e.target.value)} />
            </div>

            <div className="tr-contact-form__field">
                <label className="tr-contact-form__label"><Trans>Votre message</Trans></label>
                <textarea className="tr-contact-form__textarea" value={form.message} onChange={e => set("message", e.target.value)} />
            </div>

            <div className="tr-contact-form__submit">
                <button type="submit" className="tr-btn tr-btn--primary tr-btn--md tr-focusable">
                    <Trans>Envoyer</Trans>
                </button>
            </div>
        </form>
    );
}

export default function BethHabad({ context, loading }) {
    const { org, config, travel } = context;

    const bethHabad = applyLang(travel?.bethHabad || {}, context.lang);
    const activitySlides = bethHabad.activitySlides || [];
    const contacts = (bethHabad.contacts || []).map(c => applyLang(c, context.lang));
    const contactChannels = (bethHabad.contactChannels || []).map(c => applyLang(c, context.lang));
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);
    const about = bethHabad.about;

    const heroImage = activitySlides[0]?.image
        ? (/^https?:\/\//.test(activitySlides[0].image) ? activitySlides[0].image : `${ASSET_URL}${activitySlides[0].image}`)
        : null;

    // JSON-LD LocalBusiness pour le Beth Habad — Google l'affiche dans les résultats locaux.
    const phoneChannel = contactChannels.find(c => c.type === "phone");
    const emailChannel = contactChannels.find(c => c.type === "email");
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": org.BASE_WEB_URL ? `${org.BASE_WEB_URL}/travel/beth-chabad` : undefined,
        name: org.name,
        url: org.BASE_WEB_URL ? `${org.BASE_WEB_URL}/travel/beth-chabad` : undefined,
        image: heroImage || (org.logo && (org.logo.startsWith("http") ? org.logo : `${ASSET_URL}${org.logo}`)),
        telephone: phoneChannel?.value,
        email: emailChannel?.value,
        address: org.ville ? { "@type": "PostalAddress", addressLocality: org.ville } : undefined,
        geo: (org.lat && org.long) ? {
            "@type": "GeoCoordinates",
            latitude: parseFloat(org.lat),
            longitude: parseFloat(org.long),
        } : undefined,
        employee: contacts.map(c => ({
            "@type": "Person",
            name: c.name,
            jobTitle: c.role,
        })),
    };

    return (
        <div className="travel-page travel-page--beth-habad tr-bh">
            <style dangerouslySetInnerHTML={{ __html: bethHabadStyles }} />
            <Meta
                context={context}
                title={t`${bethHabadLabel} — ${org.name}`}
                description={config["meta.description"] || t`${bethHabadLabel} de ${org.ville || org.name} : délégués, contacts et activités`}
                image={heroImage}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}

            <Header context={context} />

            {activitySlides.length > 0 && (
                <Hero slides={activitySlides} fullWidth />
            )}

            <main className="travel-main">

                <div className="travel-container">
                    <h1 className="tr-bh__org-title">{org.name}</h1>

                    {/* ── Chabad Shluhim ── */}
                    {contacts.length > 0 && (
                        <section className="tr-section">
                            <div className="tr-section__head">
                                <Trans>Délégués 'Habad</Trans>
                            </div>
                            <div className="tr-shluhim">
                                {contacts.map(contact => (
                                    <div key={contact.id} className="tr-shluhim__card">
                                        {contact.avatar
                                            ? <img src={`${ASSET_URL}${contact.avatar}`} alt={contact.name} className="tr-shluhim__avatar" />
                                            : (
                                                <div className="tr-shluhim__avatar--placeholder">
                                                    {contact.name ? contact.name[0] : "?"}
                                                </div>
                                            )
                                        }
                                        <span className="tr-shluhim__name">{contact.name}</span>
                                        <span className="tr-shluhim__role">{contact.role}</span>
                                        {(contact.phone || contact.email || contact.whatsapp) && (
                                            <div className="tr-shluhim__actions">
                                                {contact.phone && (
                                                    <a href={`tel:${contact.phone}`} className="tr-shluhim__action tr-shluhim__action--phone" title={contact.phone}>
                                                        {renderIcon({ name: "IconPhone", source: "tabler" }, 16)}
                                                    </a>
                                                )}
                                                {contact.email && (
                                                    <a href={`mailto:${contact.email}`} className="tr-shluhim__action tr-shluhim__action--email" title={contact.email}>
                                                        {renderIcon({ name: "IconMail", source: "tabler" }, 16)}
                                                    </a>
                                                )}
                                                {contact.whatsapp && (
                                                    <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`} className="tr-shluhim__action tr-shluhim__action--whatsapp" title="WhatsApp" target="_blank" rel="noopener noreferrer">
                                                        {renderIcon({ name: "IconBrandWhatsapp", source: "tabler" }, 16)}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ── À propos du Beth Habad ── */}
                    {about && (
                        <section className="tr-section">
                            <div className="tr-section__head">
                                <Trans>À propos du {bethHabadLabel}</Trans>
                            </div>
                            <div
                                className="tr-bh__about"
                                dangerouslySetInnerHTML={{ __html: about }}
                            />
                        </section>
                    )}

                    {/* ── Adresse ── */}
                    {(() => {
                        const addressLine = [org.adresse, org.postal, org.ville].filter(Boolean).join(" ");
                        if (!addressLine) return null;
                        // Embed Google Maps sans clé API : préfère lat/long si dispo, sinon recherche par adresse.
                        const mapQuery = (org.lat && org.long)
                            ? `${org.lat},${org.long}`
                            : encodeURIComponent(addressLine);
                        const mapLanguage = context.langValue === "gr" ? "el" : (context.langValue || "fr");
                        const mapSrc = `https://maps.google.com/maps?q=${mapQuery}&hl=${mapLanguage}&z=15&output=embed`;
                        const directionsUrl = (org.lat && org.long)
                            ? `https://www.google.com/maps/dir/?api2=1&destination=${org.lat},${org.long}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`;
                        return (
                            <section className="tr-section">
                                <div className="tr-section__head">
                                    <Trans>Comment se rendre au {bethHabadLabel}</Trans>
                                </div>
                                <div className="tr-address">
                                    <span className="tr-address__label">
                                        <Trans>Adresse du {bethHabadLabel}</Trans>
                                    </span>
                                    <span className="tr-address__line">{addressLine}</span>
                                    <a
                                        href={directionsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="tr-btn tr-btn--primary tr-btn--md tr-focusable tr-address__cta"
                                        style={{ textDecoration: "none" }}
                                    >
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 16)}
                                        <Trans>Accéder au {bethHabadLabel}</Trans>
                                    </a>
                                    <iframe
                                        className="tr-address__map"
                                        src={mapSrc}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        allowFullScreen
                                        title={`Carte — ${org.name}`}
                                    />
                                </div>
                            </section>
                        );
                    })()}

                    {/* ── Contact ── */}
                    <section className="tr-section">
                        <div className="tr-contact-wrap">
                            {/* Gauche — canaux de contact */}
                            <div>
                                <div className="tr-section__head">
                                    <Trans>Nous sommes joignables ici</Trans>
                                </div>
                                <div className="tr-contact-list">
                                    {contactChannels.map(ch => {
                                        const isLink = ch.value?.startsWith("http");
                                        const href = ch.type === "phone" ? `tel:${ch.value}`
                                            : ch.type === "email" ? `mailto:${ch.value}`
                                            : ch.value;
                                        return (
                                            <a
                                                key={ch.id}
                                                href={href}
                                                className="tr-contact-item"
                                                target={isLink ? "_blank" : undefined}
                                                rel={isLink ? "noopener noreferrer" : undefined}
                                            >
                                                <ContactIcon type={ch.type} />
                                                <span className="tr-contact-item__label">
                                                    {ch.label || ch.value}
                                                    {ch.label && <span className="tr-contact-item__sublabel">{ch.value}</span>}
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Droite — formulaire */}
                            <div>
                                <div className="tr-section__head">
                                    <Trans>Nous sommes également joignables par e-mail</Trans>
                                </div>
                                <ContactForm context={context} />
                            </div>
                        </div>
                    </section>

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
